import {
    ActionType,
    ActivityRecordType,
    CalendarDate,
    maxActivitiesReportRangeDays,
    maxActivitiesReportRangeYears,
    maxActivitiesReportStaffFilter,
    missingStaffMemberName,
    RegionId,
    unnamedStaffMemberName,
    User,
    UserId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Stage } from "components/layout";
import {
    defaultGoalDatePreset,
    goalDatePresets,
    GoalDateRange,
    isSingleWholeMonth,
    priorPeriod,
    rangeDayCount,
    rangeLabel,
} from "components/marketing-goals/date-range";
import { DateRangeControl } from "components/reports/date-presets";
import { trpc } from "components/rpc/trpc";
import {
    ActivityTypeOption,
    useActivityTypeOptionsState,
} from "components/tasks/activity-type-options";
import { InlineBanner } from "components/ui/inline-banner";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { useUserOptionsForRegion } from "components/ui/use-user-options";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Redirect,
    Route,
    Switch,
    useHistory,
    useLocation,
    useParams,
} from "react-router";
import { loadAllUsers } from "store/actions";
import { UserState } from "store/reducers/users";
import {
    useCrmMode,
    useIsAllowed,
    useNow,
    useRegionId,
    useUsers,
} from "store/selectors/hooks";
import { recordTypesForCrmMode } from "util/crm-mode";
import { ActivitiesLeaderboard } from "./activities-leaderboard";
import {
    ActivitiesReportFilters,
    activitiesReportPath,
    activitiesReportRoutePaths,
    activitiesReportSearch,
    activitiesReportStaffPath,
    activityColumnsFor,
    leaderboardRows,
    LeaderboardSort,
    nameSortKey,
    organizationTypesForQuery,
    readActivitiesReportFilters,
    reportQueryOptions,
    sortLeaderboard,
    toActivitiesReportCounts,
    withRecordTypesUsedInOffice,
} from "./activities-report";
import { CountModeToggle, RecordTypeFilter } from "./activities-report-filters";
import {
    ActivitiesExportBanner,
    ExportActivitiesButton,
    useActivitiesExport,
} from "./export-activities-button";
import { StaffActivityDetail } from "./staff-activity-detail";

type StaffMemberLookup =
    | { kind: "loading" }
    | { kind: "failed" }
    | { kind: "found"; name: string }
    | { kind: "missing" };

const foundStaffMemberName = (user: User): string => user.name || unnamedStaffMemberName;

const lookUpStaffMember = (users: UserState, userId: UserId): StaffMemberLookup => {
    const user = users.users[userId];
    if (user) {
        return { kind: "found", name: foundStaffMemberName(user) };
    }
    if (users.usersLoadedOnce) {
        return { kind: "missing" };
    }
    return users.usersLoadFailed ? { kind: "failed" } : { kind: "loading" };
};

const loadedStaffMemberName = (users: UserState, userId: UserId): string => {
    const user = users.users[userId];
    return user ? foundStaffMemberName(user) : missingStaffMemberName;
};

const staffMemberName = (staffMember: StaffMemberLookup): string | null => {
    switch (staffMember.kind) {
        case "loading":
        case "failed":
            return null;
        case "found":
            return staffMember.name;
        case "missing":
            return missingStaffMemberName;
        default: {
            const exhaustiveCheck: never = staffMember;
            throw new Error(
                `Unhandled staff member lookup: ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

const staffPageTitle = (staffMember: StaffMemberLookup): string => {
    const name = staffMemberName(staffMember);
    return name === null ? "Activities" : `Activities · ${name}`;
};

const StaffNamesFailedBanner: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
    <RetryableErrorBanner
        message="Failed to load staff names."
        onRetry={onRetry}
        retrying={false}
    />
);

const StaffMemberNotice: React.FC<{
    staffMember: StaffMemberLookup;
    onRetry: () => void;
}> = ({ staffMember, onRetry }) => {
    if (staffMember.kind === "failed") {
        return <StaffNamesFailedBanner onRetry={onRetry} />;
    }
    if (staffMember.kind === "missing") {
        return (
            <InlineBanner type="warning">
                This staff member no longer exists.
            </InlineBanner>
        );
    }
    return null;
};

export const ActivitiesReport: React.FC = () => (
    <Switch>
        <Route exact path={activitiesReportRoutePaths} component={ActivitiesReportPage} />
        <Route render={() => <Redirect to={activitiesReportPath} />} />
    </Switch>
);

const CenteredMessage: React.FC<{
    tone: "info" | "error";
    children: React.ReactNode;
}> = ({ tone, children }) => (
    <div
        className={`py-12 text-center text-sm ${
            tone === "error" ? "text-red-600" : "text-gray-600"
        }`}>
        {children}
    </div>
);

const useReportFilters = (today: CalendarDate) => {
    const location = useLocation();
    const history = useHistory();
    const presets = useMemo(() => goalDatePresets(today), [today]);
    const defaultPreset = useMemo(() => defaultGoalDatePreset(today), [today]);
    const filters = useMemo(
        () => readActivitiesReportFilters(location.search, presets, defaultPreset),
        [location.search, presets, defaultPreset],
    );
    const setFilters = (next: ActivitiesReportFilters) =>
        history.replace({
            pathname: location.pathname,
            search: activitiesReportSearch(next, defaultPreset),
        });
    return { filters, setFilters, presets, search: location.search };
};

interface LeaderboardViewProps {
    regionId: RegionId | null;
    filters: ActivitiesReportFilters;
    onFiltersChange: (filters: ActivitiesReportFilters) => void;
    range: GoalDateRange;
    search: string;
    availableRecordTypes: ActivityRecordType[];
    activityTypes: ActivityTypeOption[];
    lookupsLoading: boolean;
    nameOf: (userId: UserId) => string;
    sort: LeaderboardSort;
    onSort: (sort: LeaderboardSort) => void;
}

const LeaderboardView: React.FC<LeaderboardViewProps> = props => {
    const { filters, onFiltersChange, range } = props;
    const trpcClient = trpc.useTRPC();
    const staffOptions = useUserOptionsForRegion(props.regionId);
    const countsQuery = useQuery({
        ...trpcClient.activitiesReport.counts.queryOptions({
            regionId: props.regionId ?? ("" as RegionId),
            startDate: range.start.toString(),
            endDate: range.end.toString(),
            recordTypes: filters.recordTypes,
            organizationTypes: organizationTypesForQuery(filters.organizationTypes),
            staffUserIds: filters.staffUserIds,
        }),
        enabled: !!props.regionId,
        ...reportQueryOptions(),
    });
    const counts = useMemo(
        () => toActivitiesReportCounts(countsQuery.data ?? []),
        [countsQuery.data],
    );
    const columns = useMemo(
        () =>
            activityColumnsFor(
                filters.countMode,
                counts,
                props.activityTypes,
                filters.recordTypes,
            ),
        [filters.countMode, counts, props.activityTypes, filters.recordTypes],
    );
    const activitiesExport = useActivitiesExport();
    const rows = sortLeaderboard(
        leaderboardRows(counts, columns, filters.staffUserIds, props.nameOf),
        props.sort,
    );

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 pb-4">
                <div className="w-full sm:w-96">
                    <SpMultiSelect
                        label="Staff member"
                        placeholder="All staff members"
                        options={staffOptions}
                        value={filters.staffUserIds}
                        onChange={staffUserIds =>
                            onFiltersChange({ ...filters, staffUserIds })
                        }
                    />
                    {filters.staffUserIds.length >= maxActivitiesReportStaffFilter ? (
                        <p className="mt-1 text-xs text-gray-500">
                            You can choose up to {maxActivitiesReportStaffFilter} staff
                            members.
                        </p>
                    ) : null}
                </div>
                <CountModeToggle
                    value={filters.countMode}
                    onChange={countMode => onFiltersChange({ ...filters, countMode })}
                />
            </div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <RecordTypeFilter
                    filters={filters}
                    availableRecordTypes={props.availableRecordTypes}
                    onChange={onFiltersChange}
                />
                {props.regionId &&
                countsQuery.isSuccess &&
                rows.some(row => row.total > 0) ? (
                    <ExportActivitiesButton
                        exportRequest={activitiesExport}
                        scope={{
                            regionId: props.regionId,
                            startDate: range.start.toString(),
                            endDate: range.end.toString(),
                            filters,
                            columnFilter: { kind: "all" },
                        }}
                    />
                ) : null}
            </div>
            <div className="mb-3 empty:hidden">
                <ActivitiesExportBanner exportRequest={activitiesExport} />
            </div>
            {countsQuery.isLoading || props.lookupsLoading ? (
                <CenteredMessage tone="info">Loading report...</CenteredMessage>
            ) : countsQuery.isError ? (
                <RetryableErrorBanner
                    message="Failed to load report."
                    onRetry={() => void countsQuery.refetch()}
                    retrying={countsQuery.isFetching}
                />
            ) : rows.length === 0 ? (
                <CenteredMessage tone="info">
                    No activity recorded for {rangeLabel(range)} that matches these
                    filters.
                </CenteredMessage>
            ) : (
                <>
                    <ActivitiesLeaderboard
                        rows={rows}
                        columns={columns}
                        sort={props.sort}
                        onSort={props.onSort}
                        staffHref={row =>
                            `${activitiesReportStaffPath(row.userId)}${props.search}`
                        }
                    />
                    <p className="mt-3 text-xs text-gray-500">
                        Counts cover {rangeLabel(range)}. Click a name to see that
                        person&apos;s activity.
                        {filters.staffUserIds.length === 0
                            ? " People with no activity in this range are left out. Use the Staff member filter to check on someone specific."
                            : ""}
                    </p>
                </>
            )}
        </>
    );
};

const ActivitiesReportPage: React.FC = () => {
    const regionId = useRegionId();
    const canManage = useIsAllowed(ActionType.UpdateRegionSettings);
    const now = useNow();
    const availableRecordTypes = recordTypesForCrmMode(useCrmMode().mode);
    const today = useMemo(() => CalendarDate.fromLocalDate(now), [now]);
    const reportFilters = useReportFilters(today);
    const { setFilters, presets, search } = reportFilters;
    const filters = withRecordTypesUsedInOffice(
        reportFilters.filters,
        availableRecordTypes,
    );
    const { staffUserId } = useParams<{ staffUserId?: UserId }>();
    const users = useUsers();
    const dispatch = useDispatch();
    const retryLoadingStaffNames = (): void => {
        void loadAllUsers(dispatch);
    };
    const { options: activityTypes, isLoading: activityTypesLoading } =
        useActivityTypeOptionsState();
    const [sort, setSort] = useState<LeaderboardSort>({
        key: nameSortKey,
        direction: "asc",
    });

    const range: GoalDateRange = {
        start: filters.range.startDate,
        end: filters.range.endDate,
    };
    const prior = priorPeriod(range);
    const priorLabel = isSingleWholeMonth(range) ? "Prior month" : "Prior period";
    const nameOf = (userId: UserId): string => loadedStaffMemberName(users, userId);

    const staffMember = staffUserId ? lookUpStaffMember(users, staffUserId) : null;
    const title = staffMember ? staffPageTitle(staffMember) : "Activities";

    const body = (): React.ReactNode => {
        if (!canManage) {
            return (
                <CenteredMessage tone="info">
                    You need office administrator access for this office to view this
                    report.
                </CenteredMessage>
            );
        }
        if (rangeDayCount(range) <= 0) {
            return (
                <CenteredMessage tone="info">
                    The end date must be on or after the start date.
                </CenteredMessage>
            );
        }
        if (rangeDayCount(range) > maxActivitiesReportRangeDays) {
            return (
                <CenteredMessage tone="info">
                    Choose a date range of {maxActivitiesReportRangeYears} years or fewer.
                </CenteredMessage>
            );
        }
        if (staffUserId) {
            return (
                <StaffActivityDetail
                    key={staffUserId}
                    regionId={regionId}
                    staffUserId={staffUserId}
                    current={{
                        startDate: range.start.toString(),
                        endDate: range.end.toString(),
                    }}
                    prior={{
                        startDate: prior.start.toString(),
                        endDate: prior.end.toString(),
                    }}
                    priorLabel={priorLabel}
                    availableRecordTypes={availableRecordTypes}
                    filters={filters}
                    onFiltersChange={setFilters}
                    activityTypes={activityTypes}
                    backHref={`${activitiesReportPath}${search}`}
                    notice={
                        staffMember ? (
                            <StaffMemberNotice
                                staffMember={staffMember}
                                onRetry={retryLoadingStaffNames}
                            />
                        ) : null
                    }
                />
            );
        }
        if (!users.usersLoadedOnce && users.usersLoadFailed) {
            return <StaffNamesFailedBanner onRetry={retryLoadingStaffNames} />;
        }
        return (
            <LeaderboardView
                regionId={regionId}
                filters={filters}
                onFiltersChange={setFilters}
                range={range}
                search={search}
                availableRecordTypes={availableRecordTypes}
                activityTypes={activityTypes}
                lookupsLoading={activityTypesLoading || !users.usersLoadedOnce}
                nameOf={nameOf}
                sort={sort}
                onSort={setSort}
            />
        );
    };

    return (
        <Stage>
            <PageHeader title={title} stackBreakpoint="md">
                <DateRangeControl
                    value={filters.range}
                    onChange={range => setFilters({ ...filters, range })}
                    presets={presets}
                />
            </PageHeader>
            <div className="min-h-full bg-gray-50 px-4 py-4 lg:px-6">{body()}</div>
        </Stage>
    );
};
